import { createFileRoute } from '@tanstack/react-router'

export const Route = createFileRoute('/legal/privacy-policy')({
  component: PrivacyPolicy,
})

function PrivacyPolicy() {
  return (
    <main
      style={{
        maxWidth: '780px',
        margin: '0 auto',
        padding: '120px 24px 80px',
      }}
    >
      <p
        style={{
          fontSize: '13px',
          color: '#3B82F6',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          fontWeight: '600',
        }}
      >
        Legal
      </p>
      <h1
        style={{
          fontSize: 'clamp(28px, 4vw, 48px)',
          fontWeight: '800',
          color: '#111827',
          margin: '0 0 8px',
          letterSpacing: '-1px',
        }}
      >
        Privacy Policy
      </h1>
      <p
        style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '56px' }}
      >
        Last updated: 21 October 2025
      </p>

      <Section title="Overview">
        GetCode operates as a virtual phone number and SMS verification platform.
        This Privacy Policy explains how we collect, use, and protect your
        personal data in accordance with applicable data protection laws,
        including GDPR.
      </Section>

      <Section title="Data We Collect">
        We collect the following categories of personal data:
        <ul>
          <li>
            <strong>Identity &amp; contact:</strong> name, email address, and
            general location.
          </li>
          <li>
            <strong>Account &amp; transaction records:</strong> purchase history,
            number usage, and session data.
          </li>
          <li>
            <strong>Technical data:</strong> IP addresses, device identifiers,
            and browser fingerprints.
          </li>
          <li>
            <strong>Usage analytics:</strong> pages visited, features used, and
            interaction patterns.
          </li>
          <li>
            <strong>Payment identifiers:</strong> transaction references (full
            card details are held only by our payment processors).
          </li>
        </ul>
      </Section>

      <Section title="How We Use Your Data">
        Your data is used to:
        <ul>
          <li>Deliver and maintain the GetCode service under our contract with you.</li>
          <li>
            Prevent fraud through risk scoring, IP analysis, and device
            verification.
          </li>
          <li>Comply with legal and regulatory obligations.</li>
          <li>
            Send marketing communications where you have given consent (opt-out
            available at any time).
          </li>
          <li>Resolve disputes and enforce our Terms of Use.</li>
        </ul>
      </Section>

      <Section title="Third-Party Sharing">
        We share data only where necessary with:
        <ul>
          <li>
            <strong>Cloud infrastructure providers</strong> hosting our platform.
          </li>
          <li>
            <strong>Analytics platforms</strong> (e.g. Google / Firebase) to
            understand usage.
          </li>
          <li>
            <strong>Payment processors</strong> (Apple, Google Play, Payssion,
            Stripe) — card details never pass through our servers.
          </li>
          <li>
            <strong>Communication services</strong> for delivering
            OTP-related notifications.
          </li>
          <li>
            <strong>Legal authorities</strong> where required by law.
          </li>
        </ul>
        We do not sell your personal data.
      </Section>

      <Section title="Security">
        We protect your data using encryption in transit and at rest, strict
        access controls based on the least-privilege principle, network
        segmentation, and regular security reviews.
      </Section>

      <Section title="Your Rights">
        You have the right to:
        <ul>
          <li>Access, correct, or delete your personal data.</li>
          <li>Object to or restrict certain processing activities.</li>
          <li>Withdraw consent for marketing at any time.</li>
          <li>Request data portability.</li>
          <li>Seek compensation for unlawful data handling.</li>
        </ul>
        To exercise any of these rights, contact us at{' '}
        <a href="mailto:support@getcode.app" style={{ color: '#3B82F6' }}>
          support@getcode.app
        </a>
        .
      </Section>

      <Section title="Children">
        GetCode is not directed at users under 18 years of age. We do not
        knowingly collect data from minors.
      </Section>

      <Section title="Policy Updates">
        We will give advance notice of any material changes to this policy.
        Continued use of the service after the effective date constitutes
        acceptance of the updated terms.
      </Section>

      <Section title="Contact">
        For privacy-related enquiries:{' '}
        <a href="mailto:support@getcode.app" style={{ color: '#3B82F6' }}>
          support@getcode.app
        </a>
      </Section>
    </main>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section style={{ marginBottom: '40px' }}>
      <h2
        style={{
          fontSize: '20px',
          fontWeight: '700',
          color: '#111827',
          margin: '0 0 12px',
          letterSpacing: '-0.3px',
        }}
      >
        {title}
      </h2>
      <div
        style={{
          fontSize: '15px',
          color: '#6B7280',
          lineHeight: '1.8',
        }}
      >
        {children}
      </div>
    </section>
  )
}
